import React, { useMemo } from 'react';

import { Card, Skeleton, Space, Typography } from 'antd';
import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { COLOR, FONT } from '@/shared/design-tokens/design-tokens';
import { formatMetricValue } from '@/shared/lib/utils';

import styles from './GaugeCard.module.css';

const { Text } = Typography;

type GaugeCardProps = {
  title: string;
  value: number;
  max: number;
  unit: string;
  isLoading?: boolean;
  precision?: number;
  icon?: React.ReactNode;
  thresholds?: {
    warning: number;
    danger: number;
  };
  totalCapacity?: number;
  capacityUnit?: string;
};

export const GaugeCard = ({
  title,
  value,
  max,
  unit,
  isLoading = false,
  icon,
  thresholds = { warning: 60, danger: 80 },
  totalCapacity,
  capacityUnit,
}: GaugeCardProps) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;

  const chartOption: EChartsOption = useMemo(() => {
    const getColor = () => {
      if (percentage >= thresholds.danger) return COLOR.RED[50];
      if (percentage >= thresholds.warning) return COLOR.ORANGE[50];
      return COLOR.GREEN[50];
    };

    return {
      series: [
        {
          type: 'gauge',
          radius: '100%',
          center: ['50%', '70%'],
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: max,
          itemStyle: {
            color: getColor(),
            shadowColor: `${getColor()}80`,
            shadowBlur: 10,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
          progress: {
            show: true,
            width: 28,
          },
          pointer: {
            show: false,
          },
          axisLine: {
            lineStyle: {
              width: 28,
              color: [[1, COLOR.GRAY[90]]],
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          anchor: {
            show: false,
          },
          title: {
            show: false,
          },
          detail: {
            valueAnimation: true,
            width: '60%',
            lineHeight: 40,
            borderRadius: 8,
            offsetCenter: [0, '-5%'],
            fontSize: FONT.SIZE['2XL'],
            fontWeight: FONT.WEIGHT.EXTRA_BOLD,
            fontFamily: FONT.FAMILY.NANUMSQUARE,
            formatter: () => `${formatMetricValue(value)} ${unit}`,
            color: getColor(),
          },
          data: [
            {
              value: value,
            },
          ],
        },
        {
          type: 'gauge',
          radius: '100%',
          center: ['50%', '70%'],
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: max,
          itemStyle: {
            color: 'transparent',
          },
          progress: {
            show: false,
          },
          pointer: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              width: 28,
              color: [
                [thresholds.warning / 100, `${COLOR.GREEN[50]}25`],
                [thresholds.danger / 100, `${COLOR.ORANGE[50]}25`],
                [1, `${COLOR.RED[50]}25`],
              ],
            },
          },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: false,
          },
          detail: {
            show: false,
          },
          data: [{ value: 0 }],
        },
      ],
    };
  }, [value, max, percentage, unit, thresholds]);

  if (isLoading) {
    return (
      <Card className={styles.card}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  const warningValue = (max * thresholds.warning) / 100;
  const dangerValue = (max * thresholds.danger) / 100;

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <Space size={8}>
          {icon && <span className={styles.icon}>{icon}</span>}
          <Text className={styles.title}>
            {title}
            {totalCapacity !== undefined && capacityUnit && (
              <Text className={styles.capacity}>
                (총 {formatMetricValue(totalCapacity)} {capacityUnit})
              </Text>
            )}
          </Text>
        </Space>
      </div>
      <div className={styles.chart}>
        <ReactECharts
          option={chartOption}
          style={{ height: '240px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
        />
      </div>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: COLOR.GREEN[50] }} />
          정상 (0-{formatMetricValue(warningValue)})
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: COLOR.ORANGE[50] }} />
          경고 ({formatMetricValue(warningValue)}-{formatMetricValue(dangerValue)})
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ background: COLOR.RED[50] }} />
          위험 ({formatMetricValue(dangerValue)} 이상)
        </span>
      </div>
    </Card>
  );
};
